"use strict";
/**
 * @fileoverview Nexus面板基类 - 提供即时模式UI面板开发的核心功能
 * @description 该文件定义了Nexus框架的面板基类，支持即时模式UI渲染、
 * 主题系统集成、错误处理等功能。所有用户面板都应继承此基类。
 *
 * @author ESEngine Team
 * @version 2.0.0
 * @since 1.0.0
 * @license MIT
 *
 * @example
 * ```typescript
 * @Panel({
 *   title: '我的面板',
 *   width: 600,
 *   height: 400,
 *   dockable: true
 * })
 * class MyPanel extends NexusPanel {
 *   public onGUI(): void {
 *     NexusGUI.Label('Hello World');
 *     if (NexusGUI.Button('Click Me')) {
 *       console.log('Button clicked!');
 *     }
 *   }
 * }
 * ```
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NexusPanel = void 0;
exports.Panel = Panel;
exports.getPanelMetadata = getPanelMetadata;
exports.createPanel = createPanel;
const Types_1 = require("../Types");
const NexusExtension_1 = require("./NexusExtension");
const NexusThemeManager_1 = require("./NexusThemeManager");
const NexusErrorManager_1 = require("./NexusErrorManager");
const UI_1 = __importDefault(require("@/UI"));
/**
 * Nexus面板基类
 *
 * @description 提供即时模式UI面板开发的核心功能，包括：
 * - 即时模式GUI渲染
 * - 主题系统集成
 * - 错误处理和性能监控
 * - 面板生命周期管理
 * - 自动注册和配置生成
 *
 * @public
 * @abstract
 * @since 1.0.0
 * @version 2.0.0
 *
 * @example
 * ```typescript
 * @Panel({
 *   title: '示例面板',
 *   width: 800,
 *   height: 600,
 *   dockable: true
 * })
 * class ExamplePanel extends NexusPanel {
 *   private counter = 0;
 *
 *   public onGUI(): void {
 *     NexusGUI.Label(`计数器: ${this.counter}`);
 *     if (NexusGUI.Button('增加')) {
 *       this.counter++;
 *       this.forceUpdate();
 *     }
 *   }
 * }
 * ```
 *
 * @see {@link NexusPanelConfig} 面板配置接口
 * @see {@link NexusGUI} GUI渲染系统
 */
class NexusPanel {
    /**
     * 构造函数
     *
     * @description 初始化面板实例，应用主题并设置主题监听器
     * @param config 面板配置对象
     *
     * @since 1.0.0
     * @version 2.0.0
     */
    constructor(config) {
        /**
         * 面板DOM容器元素
         *
         * @description 面板内容渲染的DOM容器，由框架自动管理
         * @protected
         * @since 1.0.0
         */
        this.container = null;
        /**
         * 面板可见状态
         *
         * @description 标识面板当前是否可见
         * @protected
         * @since 1.0.0
         */
        this.isVisible = false;
        /**
         * 主题管理器实例
         *
         * @description 用于管理面板的主题样式，支持动态主题切换
         * @protected
         * @readonly
         * @since 1.0.0
         */
        this.themeManager = NexusThemeManager_1.ThemeManager;
        /**
         * 错误管理器实例
         *
         * @description 用于处理面板运行过程中的错误
         * @protected
         * @readonly
         * @since 1.0.0
         */
        this.errorManager = NexusErrorManager_1.ErrorManager;
        /**
         * 渲染次数计数器
         *
         * @description 记录面板的渲染次数，用于性能监控
         * @private
         * @since 2.0.0
         */
        this.renderCount = 0;
        /**
         * 最后一次渲染耗时
         *
         * @description 记录最后一次渲染的耗时（毫秒），用于性能监控
         * @private
         * @since 2.0.0
         */
        this.lastRenderTime = 0;
        this.config = config;
        // 应用主题
        if (config.theme) {
            this.applyTheme(config.theme);
        }
        // 监听主题变化
        this.setupThemeListener();
    }
    /**
     * 应用主题
     *
     * @description 根据主题名称应用相应的主题样式
     * @param themeName 主题名称
     * @protected
     * @since 2.0.0
     */
    applyTheme(themeName) {
        if (this.themeManager.getTheme(themeName)) {
            this.themeManager.setTheme(themeName);
        }
    }
    /**
     * 设置主题监听器
     *
     * @description 监听全局主题变化事件，自动更新面板主题
     * @protected
     * @since 2.0.0
     */
    setupThemeListener() {
        if (typeof window !== 'undefined') {
            window.addEventListener('nexus-theme-change', () => {
                this.handleThemeChange();
            });
        }
    }
    /**
     * 处理主题变化
     *
     * @description 主题变化时的回调函数，强制重新渲染以应用新主题
     * @protected
     * @since 2.0.0
     */
    handleThemeChange() {
        // 强制重新渲染以应用新主题
        this.forceUpdate();
    }
    /**
     * 面板初始化
     *
     * @description 面板初始化时调用，带性能监控功能。
     * 子类可以重写safeInit方法来实现自定义初始化逻辑。
     *
     * @public
     * @since 1.0.0
     * @version 2.0.0
     *
     * @example
     * ```typescript
     * public onInit(): void {
     *   super.onInit();
     *   // 自定义初始化逻辑
     * }
     * ```
     */
    onInit() {
        try {
            const startTime = performance.now();
            this.safeInit();
            const endTime = performance.now();
            console.log(`面板 ${this.config.title} 初始化完成 (${(endTime - startTime).toFixed(2)}ms)`);
        }
        catch (error) {
            this.errorManager.reportError({
                code: 'PANEL_INIT_ERROR',
                message: `面板 ${this.config.title} 初始化失败`,
                stack: error instanceof Error ? error.stack : undefined,
                source: this.config.title,
                recoverable: true
            }, {
                panelName: this.config.title,
                timestamp: Date.now()
            });
        }
    }
    /**
     * 安全的初始化方法
     *
     * @description 在错误处理包装中执行的初始化逻辑，子类可以重写
     * @protected
     * @since 2.0.0
     *
     * @example
     * ```typescript
     * protected safeInit(): void {
     *   console.log('面板初始化');
     *   // 自定义初始化逻辑
     * }
     * ```
     */
    safeInit() {
        // 默认空实现，子类可以重写
        console.log(`面板 ${this.config.title} 初始化`);
    }
    /**
     * 面板销毁
     *
     * @description 面板销毁时调用，清理GUI组件和资源。
     * 子类可以重写此方法来实现自定义清理逻辑。
     *
     * @public
     * @since 1.0.0
     *
     * @example
     * ```typescript
     * public onDestroy(): void {
     *   super.onDestroy();
     *   // 自定义清理逻辑
     *   this.clearData();
     * }
     * ```
     */
    onDestroy() {
        // 清理GUI组件
        UI_1.default.clear();
        // 默认空实现，子类可以重写
        console.log(`面板 ${this.config.title} 销毁`);
    }
    /**
     * 设置容器
     */
    setContainer(container) {
        this.container = container;
    }
    /**
     * 渲染面板 - 基于优化NexusGUI的现代系统，带性能监控
     */
    render() {
        if (!this.container)
            return;
        const startTime = performance.now();
        this.renderCount++;
        try {
            // 设置NexusGUI的容器
            UI_1.default.setContainer(this.container);
            // 调用用户定义的GUI逻辑
            this.onGUI();
            const endTime = performance.now();
            this.lastRenderTime = endTime - startTime;
            // 性能警告
            if (this.lastRenderTime > 16) { // 超过一帧时间
                console.warn(`面板 ${this.config.title} 渲染耗时过长: ${this.lastRenderTime.toFixed(2)}ms`);
            }
        }
        catch (error) {
            this.errorManager.reportError({
                code: 'PANEL_RENDER_ERROR',
                message: `面板 ${this.config.title} 渲染失败`,
                stack: error instanceof Error ? error.stack : undefined,
                source: this.config.title,
                recoverable: true
            }, {
                panelName: this.config.title,
                timestamp: Date.now(),
                additionalInfo: {
                    renderCount: this.renderCount,
                    lastRenderTime: this.lastRenderTime
                }
            });
        }
    }
    /**
     * 强制更新面板 - React系统会自动处理重新渲染
     */
    forceUpdate() {
        if (this.container) {
            // React系统会自动处理重绘，只需要触发一次渲染
            this.render();
        }
    }
    /**
     * 显示面板
     */
    show() {
        this.isVisible = true;
        if (this.container) {
            this.container.style.display = 'block';
            // 重新启动渲染
            this.render();
        }
    }
    /**
     * 隐藏面板
     */
    hide() {
        this.isVisible = false;
        if (this.container) {
            this.container.style.display = 'none';
            // 清理组件以节省资源
            UI_1.default.clear();
        }
    }
    /**
     * 获取面板配置
     */
    getConfig() {
        return this.config;
    }
    /**
     * 获取面板标题
     */
    getTitle() {
        return this.config.title;
    }
    /**
     * 是否可见
     */
    getVisible() {
        return this.isVisible;
    }
    /**
     * 自动注册面板到Nexus系统
     */
    autoRegister(name) {
        const panelName = name || this.constructor.name.toLowerCase().replace('panel', '');
        NexusExtension_1.NexusExtension.Instance.registerPanel(panelName, this);
    }
    /**
     * 自动生成Cocos面板文件
     */
    generateCocosPanel(panelName) {
        const name = panelName || this.constructor.name.toLowerCase().replace('panel', '');
        return NexusExtension_1.NexusExtension.Instance.generatePanelFile(this, name);
    }
    /**
     * 快速显示面板为独立窗口（无需复杂配置）
     * @param panelClass 面板类
     * @param config 可选配置
     */
    static showAsWindow(panelClass, config) {
        const finalConfig = {
            title: "Nexus窗口",
            width: 800,
            height: 600,
            resizable: true,
            dockable: false,
            ...config
        };
        const panel = new panelClass(finalConfig);
        // 创建独立窗口
        const windowFeatures = `width=${finalConfig.width},height=${finalConfig.height},resizable=${finalConfig.resizable ? 'yes' : 'no'},scrollbars=yes`;
        const newWindow = window.open('', '_blank', windowFeatures);
        if (!newWindow) {
            console.error('无法创建新窗口，可能被浏览器阻止');
            return panel;
        }
        // 设置窗口内容
        const htmlContent = NexusPanel.generateWindowHTML(finalConfig);
        newWindow.document.write(htmlContent);
        newWindow.document.close();
        // 初始化面板
        newWindow.addEventListener('load', () => {
            const container = newWindow.document.getElementById('nexus-content');
            if (container) {
                panel.onInit();
                panel.setContainer(container);
                panel.show();
                // React系统会自动处理渲染循环，只需要初始化一次
                panel.render();
            }
        });
        // 监听窗口关闭
        const checkClosed = () => {
            if (newWindow.closed) {
                panel.onDestroy();
            }
            else {
                setTimeout(checkClosed, 1000);
            }
        };
        setTimeout(checkClosed, 1000);
        return panel;
    }
    /**
     * 生成窗口HTML内容 - 为React渲染系统优化
     */
    static generateWindowHTML(config) {
        return `
<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${config.title}</title>
    <style>
        body {
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background-color: #2a2a2a;
            color: #fff;
            overflow: hidden;
        }
        
        #nexus-content {
            position: relative;
            width: 100vw;
            height: 100vh;
            background-color: #1a1a1a;
        }
        
        /* React会自动处理所有UI渲染，保持简洁的样式 */
        .nexus-gui-container {
            width: 100%;
            height: 100%;
            overflow: auto;
        }
    </style>
</head>
<body>
    <div id="nexus-content"></div>
</body>
</html>
        `;
    }
}
exports.NexusPanel = NexusPanel;
/**
 * 面板装饰器
 * @param config 面板配置信息
 * @returns 装饰器函数
 */
function Panel(config) {
    return function (constructor) {
        Reflect.defineMetadata(Types_1.NEXUS_PANEL_METADATA, config, constructor);
        return constructor;
    };
}
/**
 * 获取面板元数据
 * @param target 目标类
 * @returns 面板配置信息
 */
function getPanelMetadata(target) {
    return Reflect.getMetadata(Types_1.NEXUS_PANEL_METADATA, target);
}
/**
 * 快速创建面板的工厂函数
 * 这是最简化的API：开发者只需要传入渲染函数
 */
function createPanel(options) {
    let GeneratedPanel = class GeneratedPanel extends NexusPanel {
        onInit() {
            console.log(`面板 ${options.title} 初始化`);
            if (options.onInit) {
                options.onInit();
            }
        }
        onGUI() {
            UI_1.default.Label('Panel created with createPanel function');
        }
        onDestroy() {
            console.log(`面板 ${options.title} 销毁`);
            if (options.onDestroy) {
                options.onDestroy();
            }
        }
    };
    GeneratedPanel = __decorate([
        Panel({
            title: options.title,
            width: options.width || 600,
            height: options.height || 400,
            dockable: true,
            resizable: true
        })
    ], GeneratedPanel);
    return new GeneratedPanel({
        title: options.title,
        width: options.width || 600,
        height: options.height || 400,
        dockable: true,
        resizable: true
    });
}
